import React, { useEffect } from 'react';

// Конфигурация из env (.env.local) и/или localStorage
const WIDGET_SRC = import.meta.env.VITE_CRM_PHONE_WIDGET_SRC as string | undefined; // пример: https://assistel.qomeq.kz/widget.min.js
const DEFAULT_WS = (import.meta.env.VITE_CRM_PHONE_WS as string | undefined) ?? 'wss://assistel.qomeq.kz/ws';
const DEFAULT_DOMAIN = (import.meta.env.VITE_CRM_PHONE_DOMAIN as string | undefined) ?? 'assistel.qomeq.kz';
const DEFAULT_AUTOSTART = (import.meta.env.VITE_CRM_PHONE_AUTOSTART as string | undefined) ?? 'false';
const ENABLED = (import.meta.env.VITE_CRM_PHONE_ENABLED as string | undefined) ?? 'false';
const PRELOAD_SIP = ((import.meta.env.VITE_CRM_PHONE_PRELOAD_SIP as string | undefined) ?? 'true').toLowerCase() === 'true';
const SIP_SRC = (import.meta.env.VITE_CRM_PHONE_SIP_SRC as string | undefined) ?? 'https://assistel.qomeq.kz/vendor/sip.min.js';
const HIDE_WIDGET_TOGGLE = ((import.meta.env.VITE_CRM_PHONE_HIDE_TOGGLE as string | undefined) ?? 'true').toLowerCase() === 'true';
const CONFIG_URL = (import.meta.env.VITE_CRM_PHONE_CONFIG_URL as string | undefined) ?? '/api/v1/telephony/me';
const TURN_SERVERS_RAW = import.meta.env.VITE_CRM_PHONE_TURN_SERVERS as string | undefined; // JSON-массив ICE-серверов
const DEBUG_LOGS_ENABLED = ((import.meta.env.VITE_CRM_PHONE_DEBUG as string | undefined) ?? 'false').toLowerCase() === 'true';
const API_BASE_URL = import.meta.env.VITE_CRM_PHONE_API_BASE_URL as string | undefined; // базовый URL API для виджета

const debugLog = (...args: unknown[]) => {
  if (!DEBUG_LOGS_ENABLED) {
    return;
  }
  console.info(...args);
};

/**
 * CRMPhoneWidget — точечная интеграция внешнего виджета телефонии.
 * Добавляет <script> с data-* атрибутами один раз на всё приложение.
 *
 * Настройки берутся из env (VITE_*) и/или localStorage:
 * - localStorage keys: crmphone.username, crmphone.password (опционально)
 * - env: VITE_CRM_PHONE_WIDGET_SRC, VITE_CRM_PHONE_WS, VITE_CRM_PHONE_DOMAIN, VITE_CRM_PHONE_AUTOSTART, VITE_CRM_PHONE_ENABLED
 */
const CRMPhoneWidget: React.FC = () => {
  useEffect(() => {
    if (String(ENABLED).toLowerCase() !== 'true') return; // отключено конфигурацией
    if (!WIDGET_SRC) return; // не задан src внешнего скрипта

    const SCRIPT_ID = 'crmphone-widget-script';
    const existed = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    // Загружаем виджет только если явно сохранено состояние "показать"
    try {
      const savedVis = sessionStorage.getItem('crmphone.widgetActive');
      if (!existed && savedVis !== 'true') {
        debugLog('[CRMPhone] Инициализация пропущена: widgetActive!=true (по умолчанию скрыт)');
        return;
      }
    } catch { /* noop */ }
    // функция для скрытия внутренней кнопки "Телефон" у виджета (в т.ч. в теневых DOM)
    const setupHideToggle = () => {
      if (!HIDE_WIDGET_TOGGLE) return;

      const hidden = new WeakSet<HTMLElement>();

      const normalize = (s: string | null | undefined) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();

      const collectRoots = (): (Document | ShadowRoot)[] => {
        const roots: (Document | ShadowRoot)[] = [document];
        const all = document.querySelectorAll<HTMLElement>('*');
        for (const el of Array.from(all)) {
          const sr = (el as any).shadowRoot as ShadowRoot | undefined;
          if (sr) roots.push(sr);
        }
        return roots;
      };

      const matchesToggle = (el: HTMLElement): boolean => {
        const txt = normalize(el.textContent);
        if (!txt.includes('телефон')) return false;
        try {
          const cs = getComputedStyle(el);
          const isFixed = cs.position === 'fixed' || cs.position === 'sticky';
          const rect = el.getBoundingClientRect();
          const nearBR = rect.right > window.innerWidth - 260 && rect.bottom > window.innerHeight - 260;
          return isFixed && nearBR;
        } catch {
          return false;
        }
      };

      const applyHideStyles = (el: HTMLElement) => {
        try {
          el.style.setProperty('visibility', 'hidden', 'important');
          el.style.setProperty('opacity', '0', 'important');
          el.style.setProperty('pointer-events', 'none', 'important');
          el.style.setProperty('width', '0px', 'important');
          el.style.setProperty('height', '0px', 'important');
          el.style.setProperty('transform', 'scale(0)', 'important');
        } catch { /* noop */ }
      };

      const tryHideOnce = (): boolean => {
        let applied = false;
        const roots = collectRoots();
        for (const root of roots) {
          const candidates = root.querySelectorAll<HTMLElement>('button, [role="button"], a, div, span');
          for (const el of Array.from(candidates)) {
            if (hidden.has(el)) continue;
            if (!matchesToggle(el)) continue;
            try {
              applyHideStyles(el);
              hidden.add(el);
              applied = true;
            } catch { /* noop */ }
          }
          // Попытка скрыть переключатель, реализованный как iframe (например, cross-origin)
          const iframes = root.querySelectorAll('iframe');
          for (const fr of Array.from(iframes)) {
            if (!(fr instanceof HTMLIFrameElement) || hidden.has(fr as any)) continue;
            try {
              const cs = getComputedStyle(fr);
              const rect = fr.getBoundingClientRect();
              const nearBR = rect.right > window.innerWidth - 260 && rect.bottom > window.innerHeight - 260;
              const isFixed = cs.position === 'fixed' || cs.position === 'sticky';
              const small = rect.width <= 160 && rect.height <= 160; // типичный размер кнопки/тогглера
              if (nearBR && isFixed && small) {
                applyHideStyles(fr as any);
                hidden.add(fr as any);
                applied = true;
              }
            } catch { /* noop */ }
          }
        }
        if (applied) {
          debugLog('[CRMPhone] Внутренний переключатель «Телефон» скрыт');
        }
        return applied;
      };

      // Перехват добавления DOM-узлов, чтобы скрыть кнопку сразу при вставке
      const enableAppendInterceptor = () => {
        const patchKey = '__crmphoneAppendPatched__' as const;
        if ((window as any)[patchKey]) return () => {};
        (window as any)[patchKey] = true;
        const origAppend = Node.prototype.appendChild;
        const origInsert = Node.prototype.insertBefore;
        const safeCheck = (node: Node) => {
          try {
            if (node instanceof HTMLElement) {
              if (matchesToggle(node)) {
                applyHideStyles(node);
                hidden.add(node);
              }
              // также проверим детей
              node.querySelectorAll && node.querySelectorAll('button, [role="button"], a, div, span').forEach((el) => {
                if (el instanceof HTMLElement && matchesToggle(el)) {
                  applyHideStyles(el);
                  hidden.add(el);
                }
              });
            }
          } catch { /* noop */ }
        };
        Node.prototype.appendChild = function(this: Node, child: any): any {
          const res = (origAppend as any).call(this, child) as any;
          safeCheck(child);
          return res;
        } as any;
        Node.prototype.insertBefore = function(this: Node, newNode: any, referenceNode: any): any {
          const res = (origInsert as any).call(this, newNode, referenceNode) as any;
          safeCheck(newNode);
          return res;
        } as any;
        return () => {
          Node.prototype.appendChild = origAppend;
          Node.prototype.insertBefore = origInsert;
          (window as any)[patchKey] = false;
        };
      };

      // Запуски: сразу, затем несколько попыток с задержками
      const disableInterceptor = enableAppendInterceptor();
      tryHideOnce();
      const timeouts = [200, 600, 1200, 2500, 5000, 8000].map((ms) => setTimeout(tryHideOnce, ms));

      // Наблюдатель DOM (оставляем активным, он лёгкий; отключим при уходе со страницы)
      const mo = new MutationObserver(() => tryHideOnce());
      mo.observe(document.documentElement, { childList: true, subtree: true });

      // Очистка таймеров при навигации
      window.addEventListener('beforeunload', () => {
        for (const id of timeouts) clearTimeout(id);
        mo.disconnect();
        disableInterceptor();
      }, { once: true });
    };
    if (existed) {
      setupHideToggle();
      return; // уже добавлен виджет
    }


    const loadScript = (src: string, id?: string) =>
      new Promise<void>((resolve, reject) => {
        // Если уже загружен
        if (id && document.getElementById(id)) return resolve();
        const el = document.createElement('script');
        if (id) el.id = id;
        el.async = true;
        el.src = src;
        el.onload = () => resolve();
        el.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(el);
      });

    const injectWidget = (cfg: { ws: string; domain: string; username?: string; password?: string }) => {
      // Установим ICE/TURN сервера до загрузки виджета (приоритет: ENV, затем localStorage)
      try {
        let servers: unknown | undefined;
        if (TURN_SERVERS_RAW) {
          try { servers = JSON.parse(TURN_SERVERS_RAW); } catch { servers = undefined; }
        }
        if (!Array.isArray(servers) || (Array.isArray(servers) && servers.length === 0)) {
          const fromLS = localStorage.getItem('ICE_TURN_SERVERS');
          if (fromLS) {
            try { servers = JSON.parse(fromLS); } catch { servers = undefined; }
          }
        }
        if (Array.isArray(servers) && servers.length > 0) {
          (window as any).ICE_TURN_SERVERS = servers;
          try {
            debugLog('[CRMPhoneWidget] Using TURN servers:', servers.map((s: any) => s?.urls || s).join(', '));
          } catch (logError) {
            console.warn('[CRMPhoneWidget] Failed to log TURN servers', logError);
          }
        }
      } catch { /* noop */ }
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.async = true;
      // Сначала заполнить data-* атрибуты, затем src
      s.dataset.ws = cfg.ws || DEFAULT_WS;
      s.dataset.domain = cfg.domain || DEFAULT_DOMAIN;
      if (cfg.username) s.dataset.username = cfg.username;
      if (cfg.password) s.dataset.password = cfg.password;
      if (CONFIG_URL) {
        // Укажем URL конфигурации, из которого виджет подтянет креды
        s.dataset.configUrl = CONFIG_URL;
        s.setAttribute('data-config-url', CONFIG_URL);
      }
      // Укажем источник sip.js для виджета (на случай если он сам подгружает зависимость)
      if (SIP_SRC) {
        s.dataset.sipSrc = SIP_SRC;
        s.setAttribute('data-sip-src', SIP_SRC);
      }
      // Укажем базовый URL API для виджета
      if (API_BASE_URL) {
        s.dataset.apiBaseUrl = API_BASE_URL;
        s.setAttribute('data-api-base-url', API_BASE_URL);
      }
      // Автостарт включаем только если уже есть логин и пароль
      const wantAutostart = String(DEFAULT_AUTOSTART).toLowerCase() === 'true';
      // Автостарт только при наличии явных cred'ов
      const canAutostart = Boolean(cfg.username && cfg.password);
      s.dataset.autostart = wantAutostart && canAutostart ? 'true' : 'false';
      if (wantAutostart && !canAutostart) {
        debugLog('[CRMPhone] AUTOSTART=on, но нет username/password — автозапуск отключен до ввода cred\'ов');
      }
      s.src = WIDGET_SRC!;
      document.body.appendChild(s);
    };

    const ensureSip = async () => {
      if (!PRELOAD_SIP) return;
      // Если глобальный SIP уже есть — пропускаем
      if (typeof (window as any).SIP !== 'undefined') return;
      try {
        await loadScript(SIP_SRC, 'crmphone-sip');
        debugLog('[CRMPhone] sip.js предзагружен:', SIP_SRC);
      } catch (e) {
        console.warn('[CRMPhone] Не удалось предзагрузить sip.js', e);
      }
    };

    (async () => {
      await ensureSip();
      const cfg = { ws: DEFAULT_WS, domain: DEFAULT_DOMAIN, username: '', password: '' };
      injectWidget(cfg);
      setupHideToggle();
      // Включаем защиту от ухода со страницы во время звонка, когда API станет доступен
      const guard = () => {
        try {
          (window as any).CRMPhone?.enableUnloadGuard?.(true);
        } catch (guardError) {
          console.warn('[CRMPhoneWidget] Failed to enable unload guard', guardError);
        }
      };
      guard();
      const tm = window.setInterval(() => { guard(); if ((window as any).CRMPhone) window.clearInterval(tm); }, 300);
    })();

    return () => {
      // Не удаляем скрипт при размонтировании, чтобы избежать повторной инициализации при навигации/HMR
    };
  }, []);

  return null;
};

export default CRMPhoneWidget;
